import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useCan } from "../../_lib/rbac/use-can";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { linkToEpocusSettings } from "../../links";
import { BackToSettingsLink } from "../_lib/back-to-settings-link";
import { SettingsCard } from "../_lib/settings-card";
import { useCanManageProgramUsers } from "../_lib/use-can-manage-program-users";
import { useEpocusProgramConfigQuery } from "./_lib/__generated__/program-config-operations.gql";
import { DemoWorklistCard } from "./_lib/demo-worklist-card";
import { EnabledDevicesCard } from "./_lib/enabled-devices-card";
import { ExamTypesCard } from "./_lib/exam-types-card";
import { ProgramSetupCard } from "./_lib/program-setup-card";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const Message: FCwC = ({ children }) => (
  <SettingsCard>
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {children}
    </Typography>
  </SettingsCard>
);

export const EpocusProgramConfigPage: FC = () => {
  const canManage = useCanManageProgramUsers();
  const canSeedWorklist = useCan("seedWorklist");
  const canManageBilling = useCan("manageBilling");
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;
  const { data, error: configError } = useEpocusProgramConfigQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (data) setLoaded(true);
  }, [data]);

  if (!canManage) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  return (
    <PageRoot>
      <BackToSettingsLink />
      <Box>
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            fontSize: rem(24),
            lineHeight: 1.2,
          }}
        >
          Program configuration
        </Typography>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginTop: 4,
          }}
        >
          How ePOCUS behaves for everyone in {program?.name ?? "your program"}
        </Typography>
      </Box>

      {!loaded && !configError && (programsLoading || programId !== null) ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading your program" />
        </LoadingRow>
      ) : !loaded && configError ? (
        <Message>Couldn't load your program configuration.</Message>
      ) : !loaded || !programId ? (
        <Message>Couldn't determine your program.</Message>
      ) : (
        <>
          <ProgramSetupCard
            programId={programId}
            config={data?.programConfig}
          />
          <ExamTypesCard programId={programId} config={data?.programConfig} />
          <EnabledDevicesCard
            programId={programId}
            config={data?.programConfig}
          />
          {canSeedWorklist && <DemoWorklistCard programId={programId} />}
        </>
      )}
    </PageRoot>
  );
};
